import { AnchorHTMLAttributes } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { cn } from '@v2/shared/lib/utils';
import { carryListingState } from './filterUrl';
import { CategoryPagination } from './types';

type Props = {
  pagination: CategoryPagination;
};

const LINK_CLASS = cn(
  'flex h-9 min-w-[38px] items-center justify-center rounded-lg border border-solid px-2',
  'text-sm no-underline'
);

/**
 * Two complete strings rather than a base plus an override: `cn` is clsx, so both
 * `bg-white` and `bg-primary` would reach the element and the stylesheet would decide.
 */
const RESTING_CLASS = 'border-slate-200 bg-white text-slate-600 hover:border-primary hover:text-primary';

const CURRENT_CLASS = 'border-primary bg-primary font-bold text-white';

/**
 * Links, not buttons — and that is the one place this page deliberately parts company
 * with `Reviews/Pager`.
 *
 * Reviews pages by API call because thin paginated review addresses are a crawl cost
 * with no upside. A category listing is the opposite: `?xPage=2` is how the rest of a
 * category is reachable at all, the legacy page emitted those urls, and this conversion
 * is not allowed to change what a crawler sees. So every page stays a real href and a
 * click is a navigation.
 *
 * The window is chosen server-side, by `CategoryPageProps::pageWindow()` — a 328-page
 * category would otherwise ship 328 absolute urls in the props for a control that draws
 * seven links. So `pages` is already the list to render, in order, and all this adds is
 * an ellipsis wherever the numbers skip.
 */
export default function Pagination({ pagination }: Props) {
  const { page, pages, previousHref, nextHref } = pagination;

  if (pages.length <= 1) {
    return null;
  }

  return (
    <nav aria-label="Pages" className="mt-8 flex items-center justify-center gap-1.5">
      {previousHref && (
        <PageLink href={previousHref} aria-label="Previous page" rel="prev">
          <FontAwesomeIcon icon={faAngleLeft} aria-hidden />
        </PageLink>
      )}

      {pages.map((entry, index) => (
        <span key={entry.page} className="flex items-center gap-1.5">
          {index > 0 && entry.page > pages[index - 1].page + 1 && (
            <span className="px-1 text-slate-400" aria-hidden>
              …
            </span>
          )}

          {entry.page === page ? (
            <span aria-current="page" className={cn(LINK_CLASS, CURRENT_CLASS)}>
              {entry.page}
            </span>
          ) : (
            <PageLink href={entry.href} aria-label={`Page ${entry.page}`}>
              {entry.page}
            </PageLink>
          )}
        </span>
      ))}

      {nextHref && (
        <PageLink href={nextHref} aria-label="Next page" rel="next">
          <FontAwesomeIcon icon={faAngleRight} aria-hidden />
        </PageLink>
      )}
    </nav>
  );
}

/**
 * The href is the backend's url, unchanged, because that is what a crawler follows. A
 * click only takes over when that url is missing part of the listing's state — the price
 * band pages' sort, or a search within a category; see `carryListingState`.
 */
function PageLink({
  href,
  children,
  ...props
}: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'onClick'> & { href: string }) {
  return (
    <a
      {...props}
      href={href}
      onClick={(event) => {
        const destination = carryListingState(href, window.location.search);

        if (destination === href || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
          return;
        }

        event.preventDefault();
        window.location.href = destination;
      }}
      className={cn(LINK_CLASS, RESTING_CLASS)}
    >
      {children}
    </a>
  );
}
